import Action from '../action';
import { ActionTypes } from '../login/auth';
import { ManagerPermission } from '../permission-profiles/permission';

export class BeneficiariesPendingActions {
    upload: number;
    block: number;
    unblock: number;
    zero: number;
    cancelImport: number;
    alternativeCollectorImport: number;
    removeAlternativeCollectorImport: number;

    constructor(
        upload = 0,
        sign = 0,
        block = 0,
        unblock = 0,
        zero = 0,
        cancelImport = 0,
        alternativeCollectorImport = 0,
        removeAlternativeCollectorImport = 0,
        permissions: Array<string> = []
    ) {
        this.upload =
            (permissions.includes(ManagerPermission.beneficiariesEntitlementVerify) ? upload : 0) +
            (permissions.includes(ManagerPermission.beneficiariesSignLevelOne) ||
            permissions.includes(ManagerPermission.beneficiariesSignLevelTwo)
                ? sign
                : 0);
        this.block = permissions.includes(ManagerPermission.beneficiariesMassBlockPost) ? block : 0;
        this.unblock = permissions.includes(ManagerPermission.beneficiariesMassUnblockPost) ? unblock : 0;
        this.zero = permissions.includes(ManagerPermission.beneficiariesMassZeroPost) ? zero : 0;
        this.cancelImport =
            permissions.includes(ManagerPermission.beneficiariesSignLevelOne) ||
            permissions.includes(ManagerPermission.beneficiariesSignLevelOne)
                ? cancelImport
                : 0;
        this.alternativeCollectorImport = permissions.includes(ManagerPermission.alternativeCollectorsImportPost)
            ? alternativeCollectorImport
            : 0;
        this.removeAlternativeCollectorImport = permissions.includes(
            ManagerPermission.removeAlternativeCollectorsImportPost
        )
            ? removeAlternativeCollectorImport
            : 0;
    }
}

export class AdjustmentsPendingActions {
    users: number;
    partners: number;
    partnerUsers: number;

    constructor(users = 0, permissions: Array<string> = [], partners: number, partnerUsers: number) {
        this.users = permissions.includes(ManagerPermission.managersManagementPost) ? users : 0;
        this.partners = permissions.includes(ManagerPermission.partnerUserPost) ? partners : 0;
        this.partnerUsers = permissions.includes(ManagerPermission.partnerUserPost) ? partnerUsers : 0;
    }
}

const defaultPendingActionValue = 0;

export class PendingActionsState {
    vendorsPost: number;
    vendorUsersPost: number;
    vendorBranchesPost: number;
    beneficiaries: BeneficiariesPendingActions;
    usersPost: number;
    adjustments: AdjustmentsPendingActions;
    partnersPost: number;
    partnerUsersPost: number;
    paymentsActions: number;
    documentsUpload: number;
    permissionProfilePost: number;
    agencyPost: number;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(payload: any = {}) {
        const pendingAction = payload.pendingActions ? payload.pendingActions : {};
        const permissions = payload.permissions ? payload.permissions : [];
        this.vendorsPost = permissions.includes(ManagerPermission.vendorsPost)
            ? pendingAction.vendorsPost || defaultPendingActionValue
            : defaultPendingActionValue;
        this.vendorUsersPost = permissions.includes(ManagerPermission.vendorUserPost)
            ? pendingAction.vendorUsersPost || defaultPendingActionValue
            : defaultPendingActionValue;
        this.vendorBranchesPost = permissions.includes(ManagerPermission.vendorsPost)
            ? pendingAction.vendorBranchesPost || defaultPendingActionValue
            : defaultPendingActionValue;

        this.vendorsPost += this.vendorUsersPost || defaultPendingActionValue;
        this.vendorsPost += this.vendorBranchesPost || defaultPendingActionValue;

        this.beneficiaries = new BeneficiariesPendingActions(
            pendingAction.beneficiaryImportsPost || defaultPendingActionValue,
            pendingAction.beneficiarySignatures || defaultPendingActionValue,
            pendingAction.beneficiaryBlock || defaultPendingActionValue,
            pendingAction.beneficiaryUnblock || defaultPendingActionValue,
            pendingAction.beneficiaryZero || defaultPendingActionValue,
            pendingAction.beneficiaryImportsCancel || defaultPendingActionValue,
            pendingAction.alternativeCollectorImport || defaultPendingActionValue,
            pendingAction.removeAlternativeCollectorImport || defaultPendingActionValue,
            permissions
        );
        this.documentsUpload = pendingAction.documentsUpload || defaultPendingActionValue;
        this.usersPost = permissions.includes(ManagerPermission.managersManagementPost)
            ? pendingAction.usersPost || defaultPendingActionValue
            : defaultPendingActionValue;
        this.adjustments = new AdjustmentsPendingActions(
            pendingAction.adjustmentsUsers || defaultPendingActionValue,
            permissions,
            pendingAction.adjustmentsPartners || defaultPendingActionValue,
            pendingAction.adjustmentsPartnerUserDetails || defaultPendingActionValue
        );
        this.partnersPost = permissions.includes(ManagerPermission.partnerPost)
            ? pendingAction.partnersPost || defaultPendingActionValue
            : defaultPendingActionValue;
        this.partnersPost += permissions.includes(ManagerPermission.partnerUserPost)
            ? pendingAction.partnerUsersPost || defaultPendingActionValue
            : defaultPendingActionValue;
        this.partnerUsersPost = permissions.includes(ManagerPermission.partnerUserPost)
            ? pendingAction.partnerUsersPost || defaultPendingActionValue
            : defaultPendingActionValue;
        this.paymentsActions =
            (permissions.includes(ManagerPermission.paymentsGeneratePost)
                ? pendingAction.paymentGenerate || defaultPendingActionValue
                : defaultPendingActionValue) +
            (permissions.includes(ManagerPermission.paymentsGeneratePost)
                ? pendingAction.paymentCancelPost || defaultPendingActionValue
                : defaultPendingActionValue) +
            (permissions.includes(ManagerPermission.paymentIssuePost)
                ? pendingAction.paymentIssuePost || defaultPendingActionValue
                : defaultPendingActionValue) +
            (permissions.includes(ManagerPermission.paymentIssuePark)
                ? pendingAction.paymentIssuePark || defaultPendingActionValue
                : defaultPendingActionValue) +
            (permissions.includes(ManagerPermission.paymentsPaidPark)
                ? pendingAction.paymentParkPaid || defaultPendingActionValue
                : defaultPendingActionValue) +
            (permissions.includes(ManagerPermission.paymentsPaidPost)
                ? pendingAction.paymentPostPaid || defaultPendingActionValue
                : defaultPendingActionValue) +
            (permissions.includes(ManagerPermission.paymentsGeneratePark)
                ? pendingAction.paymentGenerationPark || defaultPendingActionValue
                : defaultPendingActionValue);
        this.permissionProfilePost = permissions.includes(ManagerPermission.profilesPost)
            ? pendingAction.permissionProfilePost || defaultPendingActionValue
            : defaultPendingActionValue;

        this.agencyPost = permissions.includes(ManagerPermission.agencyPost)
            ? pendingAction.agencyPost || defaultPendingActionValue
            : defaultPendingActionValue;
    }
}

export const ActionCreators = {};

export function pendingActionsReducer(state: PendingActionsState = new PendingActionsState(), action: Action) {
    switch (action.type) {
        case ActionTypes.loadedPendingActions:
            // Until Ukraine branch still contains remained code after the check-out I'd like to limit
            // state props only to those which are required instead of putting everything here

            const { pendingActions, permissions } = action.payload;
            return new PendingActionsState({
                permissions,
                pendingActions: {
                    adjustmentsUsers: pendingActions.adjustmentsUsers,
                    adjustmentsPartners: pendingActions.adjustmentsPartners,
                    adjustmentsPartnerUserDetails: pendingActions.adjustmentsPartnerUserDetails,
                    usersPost: pendingActions.usersPost,
                    partnerUsersPost: pendingActions.partnerUsersPost,
                    partnerPost: pendingActions.partnerPost,
                    permissionProfilePost: pendingActions.permissionProfilePost,
                    agencyPost: pendingActions.agencyPost,
                },
            });
    }

    return state;
}
